// Category resolution for the 8 locales whose citations_landing copy needs more than
// one/other (ar, cs, he, pl, ro, ru, sr, uk), ported 1:1 from
// Mentions::MentionsColor::Channels::Nnm::PLURAL_RULES
// (app/lib/mentions/mentions_color/channels/nnm.rb) -- CLDR 48 cardinal plural rules,
// restricted to integer counts (see that file's own comment on why the fraction-only
// CLDR rules are unreachable here).
//
// Deliberately NOT registered as I18n.pluralization[locale]. That's a process-global map
// i18n-js consults for every I18n.t call across the whole app, not just this page's keys --
// and other OneSky-managed bundles (e.g. global.wjs.jsx) already ship real
// one/few/many/other hashes for ru/cs/pl/ro/sr whose "one" branch hardcodes the literal
// digit "1", relying on i18n-js's binary default pluralizer only ever selecting "one" for
// count === 1 exactly (a standard, otherwise-safe i18n-js idiom). Registering a real
// CLDR-aware pluralizer for those locales would silently revive those dead keys everywhere
// else I18n.t is called with a count, and at least ru/sr would then render the literal "1"
// for a count that's merely grammatically-one (21, 31, 41, 101, ...). Callers resolve the
// category themselves and index the translation key directly instead of asking i18n-js to
// pluralize for them:
//
//   const category = citationsLandingPluralCategory(locale, count);
//   I18n.t(`citations_landing.hero.headline_citations.${category}`, { count_delimited });
const CYCLING_ONE = (count: number) => count % 10 === 1 && count % 100 !== 11;
const CYCLING_FEW = (count: number) =>
  [2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100);

type Rule = [category: string, test: (count: number) => boolean];

// Order matters within each locale's list: the first matching category wins, mirroring
// Ruby's Hash#find over Nnm::PLURAL_RULES's insertion order.
const PLURAL_RULES: Record<string, Rule[]> = {
  ar: [
    ['one', (c) => c === 1],
    ['two', (c) => c === 2],
    ['few', (c) => c % 100 >= 3 && c % 100 <= 10],
    ['many', (c) => c % 100 >= 11 && c % 100 <= 99],
  ],
  cs: [
    ['one', (c) => c === 1],
    ['few', (c) => c >= 2 && c <= 4],
  ],
  he: [
    ['one', (c) => c === 1],
    ['two', (c) => c === 2],
  ],
  pl: [
    ['one', (c) => c === 1],
    ['few', CYCLING_FEW],
  ],
  ro: [
    ['one', (c) => c === 1],
    ['few', (c) => c !== 1 && c % 100 >= 1 && c % 100 <= 19],
  ],
  ru: [
    ['one', CYCLING_ONE],
    ['few', CYCLING_FEW],
  ],
  sr: [
    ['one', CYCLING_ONE],
    ['few', CYCLING_FEW],
  ],
  uk: [
    ['one', CYCLING_ONE],
    ['few', CYCLING_FEW],
  ],
};

// Every locale not listed above takes the binary split i18n-js's own default pluralizer
// already gives for free (count === 1 -> "one", else "other") -- true CLDR for most of them
// (da, de, el, ...), and also what id/ja/ko/vi/zh's "one" key (duplicating "other") is there
// to satisfy, since none of this page's copy uses a true CLDR "other-only" concept.
export function citationsLandingPluralCategory(
  locale: string,
  count: number,
): string {
  const rules = PLURAL_RULES[locale];
  if (!rules) {
    return count === 1 ? 'one' : 'other';
  }

  const match = rules.find(([, test]) => test(count));
  return match ? match[0] : 'other';
}
